import {Icon, Menu} from '@albert-io/atomic';
import type {IconProp} from '@albert-io/atomic/atoms/Icon/Icon.react';
import type {QuestionModelV3} from '@albert-io/models';
import cx from 'classnames';
import type {List} from 'immutable';
import React, {useMemo} from 'react';
import {useShallow} from 'zustand/react/shallow';

import {ANNOTATE_IGNORE_PROPS} from 'client/components/PracticeView/Annotations/annotationTextProjection';
import type FillInTheBlankQuestionStore from 'client/components/QuestionTypes/FillInTheBlank/FillInTheBlankQuestion.store';
import {useQuestionTranslationStore} from 'client/components/QuestionTypes/shared/TranslationSection';
import {callTargetedAction} from 'client/framework';
import fillInTheBlankQuestionActions from 'client/components/QuestionTypes/FillInTheBlank/FillInTheBlankQuestion.actions';
import forceUpdateOnStateChange from 'lib/hocs/forceUpdateOnStateChange';
import {MarkdownWithSupplement} from 'client/components/QuestionTypes/shared/MarkdownWithSupplement';

import './fitb-dropdown.scss';

interface FitbItemProps {
  choices: List<any>;
  dropdownId: string;
  store: FillInTheBlankQuestionStore;
  isGuessSubmitted: boolean;
  isExplanation: boolean;
  question: QuestionModelV3;
}

function FitbItem({
  choices,
  dropdownId,
  store,
  isGuessSubmitted,
  isExplanation,
  question
}: FitbItemProps) {
  return isExplanation ? (
    <FitbPresentationalItem
      question={question}
      store={store}
      dropdownId={dropdownId}
      choices={choices}
    />
  ) : (
    <FitbDropdown
      choices={choices}
      dropdownId={dropdownId}
      store={store}
      isGuessSubmitted={isGuessSubmitted}
      question={question}
    />
  );
}

interface FitbDropdownProps {
  choices: List<any>;
  dropdownId: string;
  store: FillInTheBlankQuestionStore;
  isGuessSubmitted: boolean;
  question: QuestionModelV3;
}

function FitbDropdown({choices, dropdownId, store, isGuessSubmitted, question}: FitbDropdownProps) {
  const {showTranslations} = useQuestionTranslationStore(
    useShallow((state) => ({
      showTranslations: state.showTranslations && state.translationSettings.question
    }))
  );
  // Format dropdown options so they jive with `react-select`
  const options = useMemo(() => {
    return choices.toJS().map((choice) => ({
      value: choice.id,
      label: choice.value
    }));
  }, [choices]);

  const selectedChoiceId = store.getSelectedChoiceForDropdown(dropdownId) || undefined;

  const selectedValue =
    (selectedChoiceId && options.find(({value: choiceId}) => selectedChoiceId === choiceId)) ||
    null;

  // Temporary hack to make differently named meta keys work

  const rubricMetaKey = question.__legacy ? 'rubric' : 'question_rubric';
  const validResponse = isGuessSubmitted
    ? question.getIn(['meta', rubricMetaKey, 'validResponse'], null)
    : null;
  const isCorrectAnswer =
    validResponse && !validResponse.isEmpty() && validResponse.get(dropdownId) === selectedChoiceId;

  const placeholder = showTranslations ? 'Elige...' : 'Choose...';

  return (
    <Menu
      wrapperClassName='inline-flex'
      className={cx('fitb-menu-container', {
        'fitb-menu-container--has-selection': !!selectedValue,
        'fitb-menu-container--has-correct-answer': isGuessSubmitted && isCorrectAnswer,
        'fitb-menu-container--has-incorrect-answer': isGuessSubmitted && !isCorrectAnswer
      })}
      closeOnItemClick
    >
      <Menu.Trigger>
        {(triggerProps) => (
          <div
            {...triggerProps}
            data-testid='fitb-dropdown_select-option'
            className={cx('fitb-menu-trigger', {
              'fitb-menu-trigger--disabled': isGuessSubmitted
            })}
          >
            <span className='fitb-menu-value'>
              {selectedValue ? (
                <MarkdownWithSupplement text={selectedValue.label} />
              ) : (
                // Fenced: the placeholder is visually hidden by default but its
                // text would otherwise join the body region's anchors.
                <span className='fitb-menu-placeholder' {...ANNOTATE_IGNORE_PROPS}>
                  {placeholder}
                </span>
              )}
            </span>
            <div className='fitb-menu-indicators'>
              <Icon
                className='fitb-menu-indicator'
                {...(() => {
                  if (!isGuessSubmitted) {
                    return {icon: 'caret-down'};
                  }
                  return {
                    icon: (isCorrectAnswer ? 'circle-check' : 'circle-xmark') as IconProp,
                    iconStyle: 'regular',
                    color: isCorrectAnswer ? 'positive' : 'negative'
                  };
                })()}
              />
            </div>
          </div>
        )}
      </Menu.Trigger>
      <Menu.Content className='fitb-dropdown__menu-content'>
        {options.map((option) => (
          <Menu.Item
            key={option.value}
            // The menu renders inline (no portal) inside the body region;
            // fencing each item keeps menu text out of the region's anchors.
            {...ANNOTATE_IGNORE_PROPS}
            onClick={() => {
              if (!isGuessSubmitted) {
                callTargetedAction({
                  name: (fillInTheBlankQuestionActions as Record<string, symbol>).SELECT_OPTION,
                  payload: {
                    choiceId: option.value,
                    dropdownId
                  },
                  targetStore: store.getName()
                });
              }
            }}
            selected={selectedValue?.value === option.value}
            disabled={isGuessSubmitted}
          >
            <MarkdownWithSupplement text={option.label} />
          </Menu.Item>
        ))}
      </Menu.Content>
    </Menu>
  );
}

interface FitbPresentationalItemProps {
  choices: List<any>;
  dropdownId: string;
  store: FillInTheBlankQuestionStore;
  question: QuestionModelV3;
}

function FitbPresentationalItem({
  question,
  store,
  dropdownId,
  choices
}: FitbPresentationalItemProps) {
  const validResponse = question?.getValidResponse?.();
  const correctAnswers =
    !question || !validResponse || validResponse.isEmpty() ? store.validResponse : validResponse;
  const correctChoiceId = correctAnswers.get(dropdownId);
  const correctChoice = choices.find((choice) => choice.getId() === correctChoiceId)?.getValue();

  return (
    <div className='fitb-presentational-container'>
      <MarkdownWithSupplement text={correctChoice} className='fitb-presentational-item' />
      <div className='fitb-select__indicators'>
        <Icon icon='circle-check' color='positive' iconStyle='regular' />
      </div>
    </div>
  );
}

export default forceUpdateOnStateChange(FitbItem);
